import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatInIST } from "../utils/dateHelpers";
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { formatCarrierName, formatErrorMessage, getErrorCategory, validateOrderFields } from '../utils/carrierUtils';

const SearchUnprocessedOrdersPage = () => {
  const lottieSrc = "/animations/loader.lottie";
  const navigate = useNavigate();

  // State for the filter form
  const [formData, setFormData] = useState({
    merchant_id: '',
    order_reference_number: '',
    pickup_pincode: '',
    drop_pincode: '',
    page: 1,
    limit: 10,
    sort_field: 'updated_date_time',
    sort_direction: 'desc',
  });

  const [loading, setLoading] = useState(false);
  const [responseData, setResponseData] = useState(null);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null); // for the Details modal
  const [viewMode, setViewMode] = useState(() => {
    // Default to cards for mobile (< 768px), table for desktop
    return typeof window !== 'undefined' && window.innerWidth < 768 ? 'cards' : 'table';
  }); // 'table' or 'cards'
  const [showFilters, setShowFilters] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedErrorLogs, setExpandedErrorLogs] = useState({});
  const [retryingOrderIds, setRetryingOrderIds] = useState(new Set());

  // Set merchant_id from localStorage on mount
  useEffect(() => {
    const localMerchantId = localStorage.getItem("merchantId");
    if (!localMerchantId) {
      navigate("/login");
    } else {
      setFormData(prev => ({
        ...prev,
        merchant_id: localMerchantId,
      }));
    }
  }, [navigate]);

  // Make sure this function is defined before you use it in the JSX
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePrevPage = () => {
    if (formData.page > 1) {
      setFormData(prev => ({ ...prev, page: prev.page - 1 }));
    }
  };

  const handleNextPage = () => {
    if (responseData?.orders?.length === formData.limit) {
      setFormData(prev => ({ ...prev, page: prev.page + 1 }));
    }
  };

  const fetchUnprocessedOrders = async () => {
    setError('');
    setLoading(true);
    try {
      const authToken = localStorage.getItem("authToken");
      if (!authToken) {
        navigate("/login");
        return;
      }

      const apiUrl = `${process.env.REACT_APP_OMS_BASE_URL}${process.env.REACT_APP_OMS_SEARCH_UNPROCESSED_ORDERS}`;
      // Build a cleaned object that excludes empty-string fields
      const cleanedFormData = Object.entries(formData).reduce((acc, [key, val]) => {
        if (val !== '') {
          acc[key] = val;
        }
        return acc;
      }, {});

      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken,
        },
        body: JSON.stringify(cleanedFormData),
      });
      if (!res.ok) {
        throw new Error(`Server error: ${res.status}`);
      }
      const data = await res.json();
      let orders = Array.isArray(data.orders) ? [...data.orders] : [];
      if (formData.sort_field === 'updated_date_time') {
        orders.sort((a, b) => {
          const ta = new Date(a.updated_date_time).getTime();
          const tb = new Date(b.updated_date_time).getTime();
          return formData.sort_direction === 'asc' ? ta - tb : tb - ta;
        });
      }
      setResponseData({ ...data, orders });
    } catch (err) {
      setError(err.message || 'Failed to fetch');
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch on page load if merchant_id is available
  useEffect(() => {
    if (formData.merchant_id) {
      fetchUnprocessedOrders();
    }
  }, [
    formData.merchant_id,
    formData.page,
    formData.limit,
    formData.sort_field,
    formData.sort_direction
  ]);

  const handleFilter = (e) => {
    e.preventDefault();
    fetchUnprocessedOrders();
  };

  const handleReset = () => {
    setFormData({
      merchant_id: formData.merchant_id, // keep merchantId from local storage
      order_reference_number: '',
      pickup_pincode: '',
      drop_pincode: '',
      page: 1,
      limit: 10,
      sort_field: 'order_date_time',
      sort_direction: 'asc',
    });
    setResponseData(null);
    setError('');
  };

  // CSV export logic (example)
  const handleExportCSV = () => {
    if (!responseData || !responseData.orders?.length) return;
    const csv = generateCSV(responseData.orders);
    downloadCSV(csv, 'unprocessed_orders.csv');
  };

  const generateCSV = (orders) => {
    const headers = [
      'Order Reference Number',
      'Invoice Value',
      'Updated Date Time',
      'Item Names',
      'Pickup Poc',
      'Pickup Address',
      'Pickup City',
      'Pickup Postal Code',
      'Drop Poc',
      'Drop Address',
      'Drop City',
      'Drop Postal Code'
    ];
    let csvContent = headers.join(',') + '\n';
    orders.forEach((order) => {
      const refNum = order.order_reference_number || '';
      const invoiceVal = order.invoice_value || '';
      const updatedTime = order.updated_date_time || '';
      const itemNames = order.item_names?.join(';') || '';

      const pickupPoc = order.pickup_details?.pickup_poc || '';
      const pickupAddress = order.pickup_details?.pickup_address || '';
      const pickupCity = order.pickup_details?.pickup_city || '';
      const pickupPostal = order.pickup_details?.pickup_postal_code || '';

      const dropPoc = order.drop_details?.drop_poc || '';
      const dropAddress = order.drop_details?.drop_address || '';
      const dropCity = order.drop_details?.drop_city || '';
      const dropPostal = order.drop_details?.drop_postal_code || '';

      const row = [
        refNum,
        invoiceVal,
        updatedTime,
        itemNames,
        pickupPoc,
        pickupAddress,
        pickupCity,
        pickupPostal,
        dropPoc,
        dropAddress,
        dropCity,
        dropPostal
      ].map(value => `"${value}"`).join(',');
      csvContent += row + '\n';
    });
    return csvContent;
  };

  const downloadCSV = (csv, filename) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
  };

  const handleCloseDetails = () => {
    setSelectedOrder(null);
  };

  const handleRetryOrder = async (order) => {
    const orderRef = order.order_reference_number;
    if (!orderRef) return;
    setRetryingOrderIds(prev => new Set(prev).add(orderRef));
    try {
      const authToken = localStorage.getItem("authToken");
      const merchantId = localStorage.getItem("merchantId");
      if (!authToken) { navigate("/login"); return; }

      const apiUrl = `${process.env.REACT_APP_OMS_BASE_URL}${process.env.REACT_APP_SEARCH_ORDERS}`;
      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken,
          'x-merchant-id': merchantId,
        },
        body: JSON.stringify({ merchant_id: merchantId, order_reference_number: orderRef, limit: 1, skip: 0 }),
      });
      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const data = await res.json();
      const fetched = data?.orders?.[0];
      if (!fetched) { setError("Order not found in search results"); return; }

      if (fetched.order_reference_number) {
        fetched.order_reference_number = fetched.order_reference_number + "_retry";
      }

      const orderType = (fetched.shipment_order_type || "").toUpperCase();
      const isReverse = orderType === "REVERSE" || orderType === "RVP";
      navigate(isReverse ? "/create-reverse-order" : "/create-forward-order", { state: { cloneFrom: fetched } });
    } catch (err) {
      setError(err?.message || "Failed to fetch order for retry");
    } finally {
      setRetryingOrderIds(prev => { const n = new Set(prev); n.delete(orderRef); return n; });
    }
  };

  // Compute statistics from orders
  const statistics = useMemo(() => {
    if (!responseData?.orders) return null;

    const orders = responseData.orders;
    const totalOrders = orders.length;
    const successfulOrders = orders.filter(o =>
      o.carrier_allocation_attempt_logs?.some(log => log.is_successful_allocation)
    ).length;
    const failedOrders = totalOrders - successfulOrders;

    // Calculate total invoice value
    const totalValue = orders.reduce((sum, o) => sum + (parseFloat(o.invoice_value) || 0), 0);

    // Error categorization
    const errorCounts = {};
    orders.forEach(order => {
      order.carrier_allocation_attempt_logs?.forEach(log => {
        if (!log.is_successful_allocation && log.error_code) {
          errorCounts[log.error_code] = (errorCounts[log.error_code] || 0) + 1;
        }
      });
    });

    return {
      totalOrders,
      successfulOrders,
      failedOrders,
      totalValue,
      successRate: totalOrders > 0 ? ((successfulOrders / totalOrders) * 100).toFixed(1) : 0,
      errorCounts,
      topErrors: Object.entries(errorCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
    };
  }, [responseData]);

  // Filter orders based on search term
  const filteredOrders = useMemo(() => {
    if (!responseData?.orders || !searchTerm) return responseData?.orders || [];

    const term = searchTerm.toLowerCase();
    return responseData.orders.filter(order =>
      order.order_reference_number?.toLowerCase().includes(term) ||
      order.pickup_details?.pickup_city?.toLowerCase().includes(term) ||
      order.drop_details?.drop_city?.toLowerCase().includes(term) ||
      order.drop_details?.drop_postal_code?.includes(term)
    );
  }, [responseData, searchTerm]);

  // Helper components
  const StatusBadge = ({ success }) => (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${success
        ? 'bg-green-100 text-green-800'
        : 'bg-red-100 text-red-800'
      }`}>
      {success ? (
        <>
          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          Allocated
        </>
      ) : (
        <>
          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          Failed
        </>
      )}
    </span>
  );

  const ValidationWarningBadge = ({ order }) => {
    const validation = validateOrderFields(order);

    if (!validation.hasWarnings) return null;

    return (
      <div className="inline-flex items-center gap-1">
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold animate-pulse ${validation.criticalCount > 0
              ? 'bg-red-600 text-white'
              : 'bg-yellow-500 text-white'
            }`}
          title={validation.warnings.map(w => w.message).join(', ')}
        >
          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {validation.criticalCount > 0 ? 'Critical' : 'Warning'}
        </span>
        <span className="text-xs text-gray-600">
          {validation.warnings.length} issue{validation.warnings.length > 1 ? 's' : ''}
        </span>
      </div>
    );
  };

  const StatCard = ({ title, value, subtitle, icon, color = "blue" }) => {
    const colorClasses = {
      blue: "bg-blue-500",
      green: "bg-green-500",
      red: "bg-red-500",
      yellow: "bg-yellow-500",
      purple: "bg-purple-500",
      indigo: "bg-indigo-500"
    };

    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:p-5 hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-xs md:text-sm font-medium text-gray-600 mb-1">{title}</p>
            <p className="text-lg font-bold text-gray-900">{value}</p>
            {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
          </div>
          {icon && (
            <div className={`${colorClasses[color]} p-2 md:p-3 rounded-lg flex-shrink-0`}>
              {icon}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-50 to-blue-50 w-full mt-2">
      <div className="max-w-full px-3">

        <div className="mb-3">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-1">
                Unprocessed Orders
              </h3>
              <p className="text-sm md:text-base text-gray-600">Monitor and manage orders pending carrier allocation</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setViewMode(viewMode === 'table' ? 'cards' : 'table')}
                className="px-3 md:px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                {viewMode === 'table' ? (
                  <>
                    <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                    <span className="hidden sm:inline">Card View</span>
                    <span className="sm:hidden">Cards</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    <span className="hidden sm:inline">Table View</span>
                    <span className="sm:hidden">Table</span>
                  </>
                )}
              </button>
              <button
                onClick={handleExportCSV}
                disabled={!responseData?.orders?.length}
                className="px-3 md:px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="hidden sm:inline">Export CSV</span>
                <span className="sm:hidden">Export</span>
              </button>
            </div>
          </div>
        </div>


        <div className="mb-2">
          <div className="bg-white shadow-lg rounded-xl border border-gray-200 overflow-hidden">
            <div
              className="p-4 md:p-5 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => setShowFilters(!showFilters)}
            >
              <div className="flex items-center gap-2 md:gap-3">
                <svg className="w-5 h-5 md:w-6 md:h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <h3 className="text-lg md:text-xl font-semibold text-gray-800">
                  Advanced Filters
                </h3>
              </div>
              <svg
                className={`w-5 h-5 text-gray-600 transition-transform ${showFilters ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>

            {showFilters && (
              <div className="p-4 md:p-5 pt-0 border-t border-gray-100">
                <form onSubmit={handleFilter}>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-2">

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Order Reference #
                      </label>
                      <input
                        type="text"
                        name="order_reference_number"
                        placeholder="e.g. 171-8584960-0458700"
                        value={formData.order_reference_number}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm
                                 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                                 transition-all"
                      />
                    </div>


                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Pickup Pincode
                      </label>
                      <input
                        type="text"
                        name="pickup_pincode"
                        placeholder="e.g. 600066"
                        value={formData.pickup_pincode}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm
                                 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                                 transition-all"
                      />
                    </div>


                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Drop Pincode
                      </label>
                      <input
                        type="text"
                        name="drop_pincode"
                        placeholder="e.g. 122102"
                        value={formData.drop_pincode}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm
                                 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                                 transition-all"
                      />
                    </div>


                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Items per page
                      </label>
                      <select
                        name="limit"
                        value={formData.limit}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm
                                 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                                 transition-all"
                      >
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="20">20</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                      </select>
                    </div>


                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sort By
                      </label>
                      <select
                        name="sort_field"
                        value={formData.sort_field}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm
                                 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                                 transition-all"
                      >
                        <option value="updated_date_time">Updated Date</option>
                        <option value="order_date_time">Order Date</option>
                        <option value="invoice_value">Invoice Value</option>
                      </select>
                    </div>


                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sort Order
                      </label>
                      <select
                        name="sort_direction"
                        value={formData.sort_direction}
                        onChange={handleChange}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm
                                 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                                 transition-all"
                      >
                        <option value="desc">Newest First</option>
                        <option value="asc">Oldest First</option>
                      </select>
                    </div>


                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 opacity-0">
                        Action
                      </label>
                      <button
                        type="submit"
                        className="w-full h-[38px] px-4 bg-blue-600 text-white rounded-lg shadow-sm text-sm
                                   hover:bg-blue-700 transition-all hover:shadow-md
                                   focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                                   flex items-center justify-center gap-1.5 font-medium"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        Apply Filters
                      </button>
                    </div>


                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 opacity-0">
                        Action
                      </label>
                      <button
                        type="button"
                        onClick={handleReset}
                        className="w-full h-[38px] px-4 bg-gray-100 text-gray-700 rounded-lg text-sm
                                   hover:bg-gray-200 transition-all
                                   focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2
                                   flex items-center justify-center gap-1.5 font-medium"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Reset
                      </button>
                    </div>
                  </div>

                  {error && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                      <svg className="w-5 h-5 text-red-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <div>
                        <p className="font-semibold text-red-800">Error</p>
                        <p className="text-red-600 text-sm">{error}</p>
                      </div>
                    </div>
                  )}
                </form>
              </div>
            )}
          </div>
        </div>


        {loading ? (
          <div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col items-center justify-center py-12 md:py-20">
              <div className="w-24 h-24 md:w-32 md:h-32">
                <DotLottieReact src={lottieSrc} loop autoplay />
              </div>
              <p className="mt-4 md:mt-6 text-gray-600 font-medium text-base md:text-lg">Loading Unprocessed Orders...</p>
              <p className="mt-2 text-gray-500 text-xs md:text-sm">Please wait while we fetch the data</p>
            </div>
          </div>
        ) : (
          responseData && (
            <div>

              {filteredOrders.length > 0 && (
                <div className="mb-2 md:mb-2">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 md:pl-4 flex items-center pointer-events-none">
                      <svg className="h-4 w-4 md:h-5 md:w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      placeholder="Search by order reference, city, or pincode..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 md:pl-12 pr-4 py-2 md:py-3 text-sm bg-white border border-gray-300 rounded-lg
                               focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                               transition-all shadow-sm"
                    />
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm('')}
                        className="absolute inset-y-0 right-0 pr-3 md:pr-4 flex items-center text-gray-400 hover:text-gray-600"
                      >
                        <svg className="h-4 w-4 md:h-5 md:w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                  {searchTerm && (
                    <p className="mt-2 text-xs md:text-sm text-gray-600">
                      Found {filteredOrders.length} of {responseData.orders.length} orders
                    </p>
                  )}
                </div>
              )}

              <div className="bg-white shadow-lg rounded-xl border border-gray-200 overflow-hidden">
                {filteredOrders.length > 0 ? (
                  <>
                    <div className="p-3 px-6 border-b border-gray-200 flex items-center justify-between">
                      <div>
                        <h3 className="text-xl font-semibold text-gray-800">
                          Order Results
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">
                          Showing {filteredOrders.length} orders
                        </p>
                      </div>
                    </div>


                    {viewMode === 'table' ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                            <tr>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                Order Reference
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                Validation
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                Invoice Value
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                Route
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                Updated
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {filteredOrders.map((order, idx) => {
                              const hasSuccess = order.carrier_allocation_attempt_logs?.some(
                                log => log.is_successful_allocation
                              );
                              const validation = validateOrderFields(order);

                              return (
                                <tr
                                  key={order.order_reference_number + idx}
                                  className={`hover:bg-blue-50 transition-all duration-200 ${validation.criticalCount > 0 ? 'bg-red-50/30' : ''
                                    }`}
                                >
                                  <td className="px-6 py-4">
                                    <div className="text-sm font-medium text-gray-900">
                                      {order.order_reference_number}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1 line-clamp-1">
                                      {order.item_names?.[0] ? (
                                        order.item_names[0].length > 50
                                          ? order.item_names[0].substring(0, 50) + '...'
                                          : order.item_names[0]
                                      ) : 'No item name'}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <StatusBadge success={hasSuccess} />
                                    {!hasSuccess && (
                                      <div className="text-xs text-gray-500 mt-1">
                                        {order.carrier_allocation_attempt_logs?.length || 0} attempts
                                      </div>
                                    )}
                                  </td>
                                  <td className="px-6 py-4">
                                    <ValidationWarningBadge order={order} />
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="text-sm font-semibold text-gray-900">
                                      ₹{order.invoice_value || 0}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="flex items-center gap-2 text-sm">
                                      <div className="flex items-center gap-1">
                                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                        <span className="text-gray-900 font-medium">
                                          {order.pickup_details?.pickup_city || 'N/A'}
                                        </span>
                                      </div>
                                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                      </svg>
                                      <div className="flex items-center gap-1">
                                        <div className="w-2 h-2 rounded-full bg-red-500"></div>
                                        <span className="text-gray-900 font-medium">
                                          {order.drop_details?.drop_city || 'N/A'}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">
                                      📍 {order.drop_details?.drop_postal_code || 'N/A'}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="text-sm text-gray-900">
                                      {order.updated_date_time ? formatInIST(order.updated_date_time) : 'N/A'}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4">
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() => handleViewDetails(order)}
                                        className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-sm font-medium rounded-lg
                                                 hover:from-blue-700 hover:to-blue-800 transition-all hover:shadow-lg transform hover:scale-105
                                                 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                      >
                                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                        View
                                      </button>
                                      {!hasSuccess && (
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleRetryOrder(order); }}
                                          disabled={retryingOrderIds.has(order.order_reference_number)}
                                          className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white text-sm font-medium rounded-lg
                                                   hover:from-orange-600 hover:to-orange-700 transition-all hover:shadow-lg transform hover:scale-105
                                                   focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                                        >
                                          {retryingOrderIds.has(order.order_reference_number) ? (
                                            <>
                                              <svg className="animate-spin w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                              </svg>
                                              Retrying...
                                            </>
                                          ) : (
                                            <>
                                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                              </svg>
                                              Retry
                                            </>
                                          )}
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredOrders.map((order, idx) => {
                          const hasSuccess = order.carrier_allocation_attempt_logs?.some(
                            log => log.is_successful_allocation
                          );
                          const validation = validateOrderFields(order);

                          return (
                            <div
                              key={order.order_reference_number + idx}
                              className={`bg-white border-2 rounded-xl p-5 hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1 ${validation.criticalCount > 0
                                  ? 'border-red-300 bg-red-50/20'
                                  : 'border-gray-200'
                                }`}
                              onClick={() => handleViewDetails(order)}
                            >

                              <div className="flex items-start justify-between mb-3">
                                <div className="space-y-2">
                                  <StatusBadge success={hasSuccess} />
                                  <ValidationWarningBadge order={order} />
                                </div>
                                <div className="text-right">
                                  <span className="text-xl font-bold text-gray-900 block">
                                    ₹{order.invoice_value || 0}
                                  </span>
                                  <span className="text-xs text-gray-500">Invoice Value</span>
                                </div>
                              </div>


                              <div className="mb-3 pb-3 border-b border-gray-100">
                                <p className="text-xs text-gray-500 mb-1">Order Reference</p>
                                <p className="text-sm font-bold text-gray-900 truncate">
                                  {order.order_reference_number}
                                </p>
                                <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                                  {order.item_names?.[0] || 'No item name'}
                                </p>
                              </div>


                              <div className="space-y-2 text-sm mb-3">
                                <div className="flex items-center p-2 bg-green-50 rounded-lg">
                                  <div className="flex-shrink-0 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-2">
                                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                                    </svg>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs text-gray-600">Pickup</p>
                                    <p className="font-semibold text-gray-900 truncate">{order.pickup_details?.pickup_city || 'N/A'}</p>
                                  </div>
                                </div>

                                <div className="flex items-center justify-center">
                                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                                  </svg>
                                </div>

                                <div className="flex items-center p-2 bg-red-50 rounded-lg">
                                  <div className="flex-shrink-0 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center mr-2">
                                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs text-gray-600">Delivery</p>
                                    <p className="font-semibold text-gray-900 truncate">
                                      {order.drop_details?.drop_city || 'N/A'}
                                    </p>
                                    <p className="text-xs text-gray-500">{order.drop_details?.drop_postal_code || 'N/A'}</p>
                                  </div>
                                </div>
                              </div>


                              <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                                <div>
                                  <p className="text-xs text-gray-500">Last Updated</p>
                                  <p className="text-xs font-medium text-gray-900">
                                    {order.updated_date_time ? formatInIST(order.updated_date_time) : 'N/A'}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  {!hasSuccess && (
                                    <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold">
                                      {order.carrier_allocation_attempt_logs?.length || 0} attempts
                                    </span>
                                  )}
                                  {!hasSuccess && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleRetryOrder(order); }}
                                      disabled={retryingOrderIds.has(order.order_reference_number)}
                                      className="inline-flex items-center px-2.5 py-1 bg-orange-500 text-white text-xs font-medium rounded-lg
                                               hover:bg-orange-600 transition-all focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-1
                                               disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                      {retryingOrderIds.has(order.order_reference_number) ? (
                                        <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                      ) : (
                                        <>
                                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                          </svg>
                                          Retry
                                        </>
                                      )}
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}


                    <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
                      <button
                        onClick={handlePrevPage}
                        disabled={formData.page === 1}
                        className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700
                                 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                                 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                      >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        Previous
                      </button>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-700">
                          Page <span className="font-semibold">{formData.page}</span>
                        </span>
                      </div>
                      <button
                        onClick={handleNextPage}
                        disabled={responseData?.orders?.length !== formData.limit}
                        className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700
                                 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                                 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                      >
                        Next
                        <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="p-12 text-center">
                    <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                    <h3 className="mt-4 text-lg font-semibold text-gray-900">No orders found</h3>
                    <p className="mt-2 text-gray-600">Try adjusting your filters or search criteria</p>
                  </div>
                )}
              </div>
            </div>
          )
        )}


        {selectedOrder && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl relative animate-fadeIn max-h-[90vh] overflow-hidden flex flex-col">

              <div className="px-8 py-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 via-purple-50 to-pink-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h2 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
                      Order Details
                    </h2>
                    <p className="text-sm text-gray-600 font-mono">
                      {selectedOrder.order_reference_number}
                    </p>
                  </div>
                  <button
                    onClick={handleCloseDetails}
                    className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-white/50 rounded-lg"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>


              <div className="overflow-y-auto flex-1 px-8 py-6">

                {(() => {
                  const validation = validateOrderFields(selectedOrder);
                  if (!validation.hasWarnings) return null;

                  return (
                    <div className={`mb-6 p-5 rounded-xl border-2 animate-pulse ${validation.criticalCount > 0
                        ? 'bg-red-50 border-red-300'
                        : 'bg-yellow-50 border-yellow-300'
                      }`}>
                      <div className="flex items-start gap-4">
                        <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${validation.criticalCount > 0 ? 'bg-red-500' : 'bg-yellow-500'
                          }`}>
                          <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h3 className={`font-bold text-lg mb-2 ${validation.criticalCount > 0 ? 'text-red-900' : 'text-yellow-900'
                            }`}>
                            {validation.criticalCount > 0 ? '⚠️ Critical Issues Detected' : '⚡ Warning: Data Validation Issues'}
                          </h3>
                          <p className={`text-sm mb-3 ${validation.criticalCount > 0 ? 'text-red-800' : 'text-yellow-800'
                            }`}>
                            This order has {validation.warnings.length} validation issue{validation.warnings.length > 1 ? 's' : ''} that need attention:
                          </p>
                          <div className="space-y-2">
                            {validation.warnings.map((warning, idx) => (
                              <div key={idx} className={`flex items-start gap-2 p-3 rounded-lg ${warning.severity === 'critical'
                                  ? 'bg-red-100 border border-red-200'
                                  : 'bg-yellow-100 border border-yellow-200'
                                }`}>
                                <svg className={`w-5 h-5 flex-shrink-0 mt-0.5 ${warning.severity === 'critical' ? 'text-red-600' : 'text-yellow-600'
                                  }`} fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                                <div className="flex-1">
                                  <p className={`font-semibold text-sm ${warning.severity === 'critical' ? 'text-red-900' : 'text-yellow-900'
                                    }`}>
                                    {warning.fieldLabel}
                                  </p>
                                  <p className={`text-sm ${warning.severity === 'critical' ? 'text-red-800' : 'text-yellow-800'
                                    }`}>
                                    {warning.message}
                                  </p>
                                </div>
                                <span className={`px-2 py-1 rounded-full text-xs font-bold ${warning.severity === 'critical'
                                    ? 'bg-red-600 text-white'
                                    : 'bg-yellow-600 text-white'
                                  }`}>
                                  {warning.severity === 'critical' ? 'CRITICAL' : 'WARNING'}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}


                {selectedOrder.carrier_allocation_attempt_logs?.some(log => log.is_successful_allocation) ? (
                  <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl flex items-center gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-green-900">✅ Successfully Allocated</p>
                      <p className="text-sm text-green-700">This order has been assigned to a carrier partner</p>
                    </div>
                  </div>
                ) : (
                  <div className="mb-6 p-4 bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-200 rounded-xl flex items-center gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-red-500 rounded-full flex items-center justify-center animate-pulse">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-semibold text-red-900">❌ Allocation Failed</p>
                      <p className="text-sm text-red-700">
                        {selectedOrder.carrier_allocation_attempt_logs?.length || 0} failed attempt(s)
                      </p>
                    </div>
                  </div>
                )}


                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-5 border border-blue-100">
                    <p className="text-sm text-gray-600 mb-1">Invoice Value</p>
                    <p className="text-lg font-bold text-gray-900">
                      ₹{selectedOrder.invoice_value || 'N/A'}
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-5 border border-purple-100">
                    <p className="text-sm text-gray-600 mb-1">Last Updated</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {selectedOrder.updated_date_time ? formatInIST(selectedOrder.updated_date_time) : 'N/A'}
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-green-50 to-white rounded-xl p-5 border border-green-100">
                    <p className="text-sm text-gray-600 mb-1">Items</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {selectedOrder.item_names?.length || 0} item(s)
                    </p>
                  </div>
                </div>


                {selectedOrder.item_names && selectedOrder.item_names.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                      Order Items
                    </h3>
                    <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                      {selectedOrder.item_names.map((item, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-semibold">
                            {idx + 1}
                          </span>
                          <p className="text-sm text-gray-700 flex-1">{item}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}


                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">

                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border-2 border-green-200">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                        </svg>
                      </div>
                      <h3 className="font-semibold text-gray-900">📦 Pickup Details</h3>
                    </div>
                    {selectedOrder.pickup_details ? (
                      <div className="space-y-3 text-sm">
                        <div className={!selectedOrder.pickup_details.pickup_poc ? 'animate-pulse' : ''}>
                          <span className="text-gray-600">Contact:</span>
                          <span className={`ml-2 font-medium ${!selectedOrder.pickup_details.pickup_poc || selectedOrder.pickup_details.pickup_poc.trim() === ''
                              ? 'text-red-600 font-bold'
                              : 'text-gray-900'
                            }`}>
                            {selectedOrder.pickup_details.pickup_poc || '⚠️ MISSING'}
                          </span>
                        </div>
                        {selectedOrder.pickup_details.pickup_organisation && (
                          <div>
                            <span className="text-gray-600">Organization:</span>
                            <span className="ml-2 font-medium text-gray-900">
                              {selectedOrder.pickup_details.pickup_organisation}
                            </span>
                          </div>
                        )}
                        <div className={!selectedOrder.pickup_details.pickup_address ? 'animate-pulse' : ''}>
                          <span className="text-gray-600">Address:</span>
                          <p className={`mt-1 font-medium ${!selectedOrder.pickup_details.pickup_address || selectedOrder.pickup_details.pickup_address.trim() === ''
                              ? 'text-red-600 font-bold'
                              : 'text-gray-900'
                            }`}>
                            {selectedOrder.pickup_details.pickup_address || '⚠️ MISSING'}
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-3">
                          <span className="px-2 py-1 bg-white rounded text-xs font-medium text-gray-700">
                            {selectedOrder.pickup_details.pickup_city}
                          </span>
                          <span className="px-2 py-1 bg-white rounded text-xs font-medium text-gray-700">
                            {selectedOrder.pickup_details.pickup_state}
                          </span>
                          <span className="px-2 py-1 bg-white rounded text-xs font-medium text-gray-700">
                            {selectedOrder.pickup_details.pickup_postal_code}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-600">No pickup details available</p>
                    )}
                  </div>


                  <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-xl p-5 border-2 border-red-200">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                        </svg>
                      </div>
                      <h3 className="font-semibold text-gray-900">🎯 Drop Details</h3>
                    </div>
                    {selectedOrder.drop_details ? (
                      <div className="space-y-3 text-sm">
                        <div className={!selectedOrder.drop_details.drop_poc || selectedOrder.drop_details.drop_poc.trim() === '' ? 'animate-pulse' : ''}>
                          <span className="text-gray-600">Contact:</span>
                          <span className={`ml-2 font-medium ${!selectedOrder.drop_details.drop_poc || selectedOrder.drop_details.drop_poc.trim() === ''
                              ? 'text-red-600 font-bold'
                              : 'text-gray-900'
                            }`}>
                            {selectedOrder.drop_details.drop_poc && selectedOrder.drop_details.drop_poc.trim() !== ''
                              ? selectedOrder.drop_details.drop_poc
                              : '⚠️ MISSING - CRITICAL'}
                          </span>
                        </div>
                        <div className={!selectedOrder.drop_details.drop_address ? 'animate-pulse' : ''}>
                          <span className="text-gray-600">Address:</span>
                          <p className={`mt-1 font-medium ${!selectedOrder.drop_details.drop_address || selectedOrder.drop_details.drop_address.trim() === ''
                              ? 'text-red-600 font-bold'
                              : 'text-gray-900'
                            }`}>
                            {selectedOrder.drop_details.drop_address && selectedOrder.drop_details.drop_address.trim() !== ''
                              ? selectedOrder.drop_details.drop_address
                              : '⚠️ MISSING - CRITICAL'}
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-3">
                          <span className={`px-2 py-1 bg-white rounded text-xs font-medium ${!selectedOrder.drop_details.drop_city ? 'text-red-600' : 'text-gray-700'
                            }`}>
                            {selectedOrder.drop_details.drop_city || '⚠️ Missing City'}
                          </span>
                          <span className={`px-2 py-1 bg-white rounded text-xs font-medium ${!selectedOrder.drop_details.drop_state ? 'text-red-600' : 'text-gray-700'
                            }`}>
                            {selectedOrder.drop_details.drop_state || '⚠️ Missing State'}
                          </span>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${!selectedOrder.drop_details.drop_postal_code || selectedOrder.drop_details.drop_postal_code.trim() === ''
                              ? 'bg-red-100 text-red-800 border-2 border-red-400 animate-pulse'
                              : 'bg-white text-gray-700'
                            }`}>
                            {selectedOrder.drop_details.drop_postal_code && selectedOrder.drop_details.drop_postal_code.trim() !== ''
                              ? selectedOrder.drop_details.drop_postal_code
                              : '⚠️ MISSING'}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-600">No drop details available</p>
                    )}
                  </div>
                </div>


                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Carrier Allocation Timeline
                  </h3>
                  {selectedOrder.carrier_allocation_attempt_logs && selectedOrder.carrier_allocation_attempt_logs.length > 0 ? (
                    <div className="relative">

                      <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-200 via-purple-200 to-gray-200"></div>

                      <div className="space-y-6">
                        {selectedOrder.carrier_allocation_attempt_logs.map((log, index) => (
                          <div key={index} className="relative flex items-start gap-4 pl-0">

                            <div className={`relative z-10 flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center transform transition-all hover:scale-110 ${log.is_successful_allocation
                                ? 'bg-gradient-to-br from-green-400 to-green-600 ring-4 ring-green-100 shadow-lg'
                                : 'bg-gradient-to-br from-red-400 to-red-600 ring-4 ring-red-100 shadow-lg'
                              }`}>
                              {log.is_successful_allocation ? (
                                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              ) : (
                                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>


                            <div className={`flex-1 rounded-xl p-5 shadow-md hover:shadow-xl transition-all ${log.is_successful_allocation
                                ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200'
                                : 'bg-gradient-to-br from-red-50 to-rose-50 border-2 border-red-200'
                              }`}>
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                  <div className="flex flex-wrap items-center gap-2 mb-2">
                                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${log.is_successful_allocation
                                        ? 'bg-green-200 text-green-900'
                                        : 'bg-red-200 text-red-900'
                                      }`}>
                                      Priority #{log.attempted_carrier_partner_priority_order}
                                    </span>
                                    <span className={`px-3 py-1 rounded-full text-xs font-bold shadow-sm ${log.is_successful_allocation
                                        ? 'bg-gradient-to-r from-green-600 to-green-700 text-white'
                                        : 'bg-gradient-to-r from-red-600 to-red-700 text-white'
                                      }`}>
                                      {log.is_successful_allocation ? '✅ Success' : '❌ Failed'}
                                    </span>
                                  </div>
                                  <p className="text-lg font-bold text-gray-900 mt-2">
                                    {formatCarrierName(log.attempted_carrier_partner_id)}
                                  </p>
                                </div>
                              </div>

                              {!log.is_successful_allocation && log.error_message && (
                                <div className="mt-4 p-4 bg-white rounded-lg border-2 border-red-100 shadow-sm">
                                  <p className="text-xs font-bold text-gray-700 mb-2 flex items-center gap-2 uppercase tracking-wide">
                                    <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                    Error Details
                                  </p>
                                  <p className="text-sm text-gray-900 leading-relaxed font-medium">
                                    {formatErrorMessage(log.error_message, log.error_code)}
                                  </p>
                                  {log.error_code && (
                                    <div className="mt-3 flex flex-wrap items-center gap-2">
                                      <span className="inline-flex items-center px-2.5 py-1 bg-red-100 text-red-800 rounded-md text-xs font-bold">
                                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                        {log.error_code}
                                      </span>
                                      <span className="inline-flex items-center px-2.5 py-1 bg-orange-100 text-orange-800 rounded-md text-xs font-bold">
                                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                        </svg>
                                        {getErrorCategory(log.error_code, log.error_message)}
                                      </span>
                                    </div>
                                  )}


                                  {log.error_message.length > 100 && log.error_message.includes('OMSException') && (
                                    <div className="mt-3 pt-3 border-t border-gray-200">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setExpandedErrorLogs(prev => ({
                                            ...prev,
                                            [`${selectedOrder.order_reference_number}-${index}`]: !prev[`${selectedOrder.order_reference_number}-${index}`]
                                          }));
                                        }}
                                        className="flex items-center gap-2 text-xs text-blue-600 hover:text-blue-800 font-semibold transition-colors"
                                      >
                                        <svg
                                          className={`w-4 h-4 transition-transform ${expandedErrorLogs[`${selectedOrder.order_reference_number}-${index}`] ? 'rotate-90' : ''
                                            }`}
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                        {expandedErrorLogs[`${selectedOrder.order_reference_number}-${index}`] ? 'Hide' : 'View'} raw error message
                                      </button>
                                      {expandedErrorLogs[`${selectedOrder.order_reference_number}-${index}`] && (
                                        <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-300 max-h-48 overflow-auto">
                                          <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono break-all">
                                            {log.error_message}
                                          </pre>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}

                              <div className="mt-4 flex items-center gap-2 text-xs text-gray-600 bg-white/50 px-3 py-2 rounded-lg">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span className="font-medium">
                                  {log.created_date_time_utc ? formatInIST(log.created_date_time_utc) : 'N/A'}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-dashed border-gray-300">
                      <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="mt-4 text-gray-600 font-medium">No carrier allocation logs available</p>
                      <p className="mt-1 text-xs text-gray-500">Allocation attempts will appear here</p>
                    </div>
                  )}
                </div>
              </div>


              <div className="px-8 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
                <button
                  onClick={handleCloseDetails}
                  className="px-6 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all
                           focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};


export default SearchUnprocessedOrdersPage;